import { useState, useRef, useMemo } from "react"
import PopupContainer from "src/component/popup/PopupContainer"
import AccordionContainer from "src/component/Accordion"
import AccordionCard from "src/component/Accordion/Component"
import { useGet } from "src/hooks/useGet"
import { GetRecurringDecisionMedicines, SubmitRecurringRequest } from "src/requests/RecurringRequests"
import { RecurringDecisionMedicine, RecurringRequest, RequestDispensingType, SubmitRecurringRequestBody } from "src/types/Requests/MobRecurringRequests"
import { useTranslation } from "react-i18next"
import SubmitBtn from "src/component/ActionBtn/SubmitBtn"
import CostTag from "src/component/CostTag"
import { Alert } from "src/component/Alert"
import MedicineCardContent from "./MedicineCardContent"
import MedicineAccordionTitle from "./MedicineAccordionTitle"
import MedicineAccordionSkeleton from "./MedicineCardContent/MedicineAccordionSkeleton"
import { SummaryPopup } from "../../../../component/popup/SummaryPopup/SummaryPopup"

import "./style.css"
import { useMutation } from "src/hooks/useMutation"
import CheckBox from "src/component/CheckBox"

const ModalName = "recurring-request-medicines"

interface IRecurringRequestMedicines {
    modalID: any
    items: RecurringRequest | null
    setSelectedItems: (row: RecurringRequest | null) => void
}

const RecurringRequestMedicines = ({ modalID, items, setSelectedItems }: IRecurringRequestMedicines) => {
    const { t } = useTranslation()
    const [selectedMedicines, setSelectedMedicines] = useState<RecurringDecisionMedicine[]>([])
    const [openedMedicineId, setOpenedMedicineId] = useState<number | null>(null)
    const [requestDispensingType, setRequestDispensingType] = useState<RequestDispensingType | null>(null)
    const [isFromDonationsStock, setIsFromDonationsStock] = useState(false)
    const confirmationModalRef = useRef<HTMLButtonElement>(null)
    const pharmId = Number(sessionStorage.getItem("optionId")) || null

    const { data: medicines, setData: setMedicines, isPending: isPendingMedicines } = useGet<RecurringDecisionMedicine[]>(
        {
            API: GetRecurringDecisionMedicines,
            depArr: [items?.decisionId, pharmId, items],
            params: [items?.decisionId, pharmId],
            isNotValid: !items?.decisionId || !pharmId,
            defaultValue: [],
            needSpinner: false
        })

    const handleSelectMedicine = (medicine: RecurringDecisionMedicine, isSelected: boolean) => {
        if (isSelected) {
            setSelectedMedicines(prev => [...prev, medicine])
        } else {
            setSelectedMedicines(prev => prev.filter(filteredMed => medicine?.medicineId !== filteredMed?.medicineId))
        }
    }

    const handleToggleAccordion = (medicineId: number) => {
        setOpenedMedicineId(prev => (prev === medicineId ? null : medicineId))
    }

    const { mutate, isPending: isPendingSubmit } = useMutation()

    const handleShowConfirmation = (requestDispensingType: RequestDispensingType) => {
        if (selectedMedicines.length === 0) return
        setRequestDispensingType(requestDispensingType)
        confirmationModalRef.current?.click()
    }

    const handleConfirmSubmit = (requestDispensingType: RequestDispensingType | null) => {
        if (!items?.decisionId || !pharmId || !requestDispensingType) return
        const body: SubmitRecurringRequestBody = {
            decisionId: items?.decisionId,
            pharmacyId: pharmId,
            requestDispensingType: requestDispensingType,
            medicines: selectedMedicines.map(medicine => ({
                decisionMedicineId: medicine.decisionMedicineId,
                isAvailable: true,
                fromDonationsStock: isFromDonationsStock || false,
            }))
        }
        mutate({
            apiMethod: SubmitRecurringRequest,
            body: body,
            actionType: "post",
            name: "recurring-request-medicines",
            onSuccess: () => {
                resetFields()
                confirmationModalRef.current?.click()
            },
        })
    }

    const resetFields = () => {
        setSelectedMedicines([])
        setOpenedMedicineId(null)
        setMedicines([])
        setSelectedItems(null)
    }

    const totalCost = useMemo(() => selectedMedicines.reduce(
        (acc, medicine) => acc + medicine.price * medicine.amount,
        0
    ), [selectedMedicines])

    const confirmationHeaderItems: Record<string, string | number> = {
        "total-cost": totalCost.toFixed(2),
        "remaining-amount": items?.remainingAmount?.toFixed(2) || "0.00",
        "monthly-amount": items?.monthlyAmount?.toFixed(2) || "0.00",
    }

    const confirmationItems = selectedMedicines.map((medicine) => ({
        id: medicine.decisionMedicineId,
        name: medicine.medicineName,
        value: `${medicine.amount} × ${medicine.price.toFixed(2)} = ${(medicine.price * medicine.amount).toFixed(2)}`,
    }))

    return (
        <>
            <PopupContainer Name={ModalName} modalID={modalID} resetAllFields={resetFields}>
                <div className="recurring-medicines-wrapper">
                    <div className="recurring-medicines-header">
                        <h4 className="medicines-title">{t("medicines")}</h4>
                    </div>
                    <div className="recurring-medicines-container">

                        {isPendingMedicines ? (
                            <MedicineAccordionSkeleton />
                        ) : medicines?.length === 0 ? (
                            <Alert variant="warning" >
                                {t("noMedicinesAvailable")}
                            </Alert>
                        ) : (
                            <>
                                <div className="row">
                                    <div className="row my-4">
                                        <CostTag
                                            amount={totalCost}
                                            title={"total-cost"}
                                        />
                                        <CostTag amount={items?.remainingAmount} title={"remaining-amount"} />
                                        <CostTag amount={items?.monthlyAmount} title={"monthly-amount"} />
                                    </div>
                                </div>
                                <AccordionContainer>
                                    {medicines?.map((medicine: RecurringDecisionMedicine) => {
                                        const isSelected = selectedMedicines.some(selectedMed => selectedMed?.medicineId === medicine?.medicineId)
                                        const isOpened = openedMedicineId === medicine.decisionMedicineId
                                        const totalPrice = (medicine.price * medicine.amount).toFixed(2)

                                        return (
                                            <div key={medicine?.decisionMedicineId} className="my-2">
                                                <AccordionCard
                                                    contentClassName="medicine-card-container"
                                                    contentTitle={
                                                        <MedicineAccordionTitle
                                                            medicine={medicine}
                                                            isSelected={isSelected}
                                                            totalPrice={totalPrice}
                                                            isOpened={isOpened}
                                                            onSelect={(value: boolean) => handleSelectMedicine(medicine, value)}
                                                            onToggle={() => handleToggleAccordion(medicine.decisionMedicineId)}
                                                        />
                                                    }
                                                    isShow={isOpened}
                                                >
                                                    <MedicineCardContent medicine={medicine} />
                                                    {
                                                        medicine.availableDonatedStock > 0 && (
                                                            <CheckBox
                                                                value={isFromDonationsStock}
                                                                setValue={setIsFromDonationsStock}
                                                                label="from-donations-stock"
                                                            />
                                                        )
                                                    }
                                                </AccordionCard>
                                            </div>
                                        )
                                    })}
                                </AccordionContainer>
                            </>
                        )}
                    </div>
                    <div className="row">
                        <div className="col-sm-6">
                            <SubmitBtn
                                label={t('confirm-shipment')}
                                onSubmit={() => handleShowConfirmation(RequestDispensingType.Delivery)}
                                loading={false}
                                disabled={selectedMedicines.length === 0}
                                className="col-sm-6"
                            />
                        </div>
                        <div className="col-sm-6">
                            <SubmitBtn
                                label={t('confirm-manual-recive')}
                                onSubmit={() => handleShowConfirmation(RequestDispensingType.Manual)}
                                loading={false}
                                disabled={selectedMedicines.length === 0}
                                className="col-12 col--6 change-request"
                            />
                        </div>
                    </div>
                </div>

            </PopupContainer>
            <SummaryPopup
                modalID={confirmationModalRef}
                headerItems={confirmationHeaderItems}
                items={confirmationItems}
                onConfirm={() => handleConfirmSubmit(requestDispensingType)}
                onCancel={() => modalID?.current?.click()}
                isLoading={isPendingSubmit}
                summaryTableTitle="selected-medicines"
            />
        </>
    )
}



export default RecurringRequestMedicines